import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CometChatCoreService } from './cometChatCore.service';
import { ApiHelper } from '../apiHelper';
import { ConstantMan } from '../constantMan';
import { CometChatAuthToken } from 'anchor-shared/lib/model/entity/CometChatAuthToken';
import { Observable, of } from 'rxjs';
import { tap, catchError, map, take } from 'rxjs/operators';
import { HttpHeaders } from '@angular/common/http';
import { buildUIKitSettings } from './cometChatUtils';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class CometChatSuperAdminService {
  private authToken: CometChatAuthToken;
  private superAdminUser: any | null = null;
  private userId: string;
  private appId: string;
  private region: string;
  private CometChatUIKit: any;
  private CometChat: any;

  constructor(
    private cometChatCore: CometChatCoreService,
    private apiHelper: ApiHelper,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {}

  private async loadCometChatSDKs() {
    if (!this.CometChat || !this.CometChatUIKit) {
      const { CometChatUIKit } = await import('@cometchat/chat-uikit-angular');
      const { CometChat } = await import('@cometchat/chat-sdk-javascript');
      this.CometChatUIKit = CometChatUIKit;
      this.CometChat = CometChat;
    }
  }

  public getCometChat() {
    return this.CometChat;
  }

  public getCometChatUser() {
    return this.superAdminUser;
  }

  public isLoggedIn() {
    return !!this.superAdminUser;
  }

  public isCometChatSuperAdmin(): boolean {
    return !!this.authToken?.token;
  }

  private getSuperAdminAuthToken(): Observable<{
    authToken: CometChatAuthToken;
    userId: string;
    appId: string;
    region: string;
  }> {
    const url =
      this.apiHelper.getServiceName() +
      ConstantMan.API.RESOURCE.COMET_CHAT_AUTH_URL +
      '/super-admin/token?platform=web';

    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper
      .apiCall<{
        authToken: CometChatAuthToken;
        userId: string;
        appId: string;
        region: string;
      }>(url, 'GET', null, null, headers)
      .pipe(
        tap((res) => {
          this.authToken = res.authToken;
          this.userId = res.userId;
          this.appId = res.appId;
          this.region = res.region;
        }),
        catchError((error) => {
          console.error('Failed to fetch super admin token:', error);
          throw error;
        }),
      );
  }

  async login() {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('Skipping CometChat login on server side.');
      return;
    }

    await this.loadCometChatSDKs();

    if (!this.authToken) {
      await this.getAuthToken().pipe(take(1)).toPromise();
    }

    await this.cometChatCore.ensureCometChatLoaded().toPromise();

    const UIKitSettings = buildUIKitSettings(
      this.appId,
      this.region,
      this.authToken.token,
    );

    this.CometChatUIKit.init(UIKitSettings);

    const CometChat = this.getCometChat();

    const loggedUser = await CometChat.getLoggedinUser();
    if (!loggedUser || loggedUser?.getUid() !== this.userId) {
      this.superAdminUser = await CometChat.login({
        authToken: this.authToken.token,
      });
    } else {
      this.superAdminUser = loggedUser;
    }

    return;
  }

  public logout() {
    const CometChat = this.getCometChat();
    this.superAdminUser = null;
    return CometChat.logout();
  }

  public getSuperAdminUser(): any | null {
    return this.superAdminUser;
  }

  public getAuthToken(): Observable<CometChatAuthToken> {
    if (
      this.authToken &&
      this.authToken.expiresAt &&
      new Date() < this.authToken.expiresAt
    ) {
      return of(this.authToken);
    } else {
      return this.getSuperAdminAuthToken().pipe(map((res) => res.authToken));
    }
  }

  public getSuperAdminUid(): string {
    return this.superAdminUser?.getUid();
  }
}
